import React, { useEffect, useRef } from 'react';
import { useFormik } from 'formik';

// Components
import { Spinner, Table} from 'react-bootstrap';
import ActionButton from 'components/common/ActionButton';

import ApiService from 'helpers/apiService';

import { IProviderTypes } from './types';

type Props = {
  data: IProviderTypes[];
  loading: boolean;
 setLoading: (value: boolean) => void;
  getProviders: () => void;
};

type FormValues = {
  editProviders: number | null;
  providers: {
    [id: number]: {
      name: string;
      service: string;
    }
  }
};

const ProvidersTable = ({ data, loading, setLoading, getProviders }: Props) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: {
          editProviders: null,
          providers: {}
        },
    onSubmit: async () => {
      const editId = formik.values.editProviders;
      if (editId === null || !formik.values.providers[editId]) return;
      const updatedData = formik.values.providers[editId];
      //if (!editId || !updatedData) return;

      setLoading(true);
      try {
        await ApiService.updateProvider({
           id: editId,
           ...updatedData
         });
        getProviders();
        formik.setFieldValue('editProviders', null);
      } catch (e) {
        console.error('Ошибка при обновлении провайдера:', e);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, setFieldValue, handleSubmit } = formik;

  const handleEdit = (id: number) => {
        setFieldValue('editProviders', id);
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await ApiService.deleteProvider(id);
      getProviders();
    } catch (e) {
      console.error('Ошибка при удалении:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      wrapperRef.current &&
      !(wrapperRef?.current as any)?.contains(event.target as Node)
    ) {
      if (formik.values.editProviders) {
      handleSubmit();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!data) return;
      const initialProviders = data.reduce((acc, provider) => ({
        ...acc,
        [provider.id]: {
          name: provider.name,
          service: provider.service
        }
      }), {});

  formik.setValues({
    editProviders: null,
    providers: initialProviders
  });
}, [data]);

  if (loading) return <Spinner animation="border" />;

  return (
    <div ref={wrapperRef}>
      <Table responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>Имя</th>
            <th>Наименование адаптера</th>
            <th className="text-end">Действия</th>
          </tr>
        </thead>
        <tbody>
          {data.map((provider) => {
            const isEditing = values.editProviders === provider.id;
            return (
              <tr key={provider.id}>
                <td>{provider.id}</td>
                <td>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formik.values.providers[provider.id]?.name || ''}
                      onChange={(e) =>
                        formik.setFieldValue(`providers.${provider.id}.name`, e.target.value)
                      }
                    />
                  ) : (
                    provider.name
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="text"
                      value={values.providers[provider.id]?.service || ''}
                      onChange={(e) =>
                        setFieldValue(`providers.${provider.id}.service`, e.target.value)
                      }
                    />
                  ) : (
                    provider.service
                  )}
                </td>
                <td className="text-end">
                  <ActionButton
                    icon="edit"
                    title="Редактировать"
                    variant="action"
                    onClick={() => handleEdit(provider.id)}
                    className="p-0 me-2 border-0 bg-transparent shadow-none"
                    disabled={loading}
                  />
                  <ActionButton
                    icon="trash-alt"
                    title="Удалить"
                    variant="action"
                    onClick={() => handleDelete(provider.id)}
                    className="p-0 border-0 bg-transparent shadow-none"
                    disabled={loading}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default ProvidersTable;
